import { memo } from 'react';
import { Tag } from 'lucide-react';

export type PriceDisplayVariant = 'default' | 'featured' | 'compact' | 'inline' | 'large';

interface PriceDisplayProps {
  price: number;
  originalPrice?: number | null;
  isOnSale?: boolean;
  discountPercentage?: number | null;
  offerDays?: number | null;
  variant?: PriceDisplayVariant;
  showDiscountBadge?: boolean;
  showOfferDays?: boolean;
  className?: string;
}

/**
 * Componente unificado para mostrar precios de productos.
 * Reemplaza las múltiples implementaciones inconsistentes de display de precios.
 *
 * @example
 * // Precio simple
 * <PriceDisplay price={99.99} />
 *
 * // Con oferta
 * <PriceDisplay
 *   price={79.99}
 *   originalPrice={99.99}
 *   isOnSale={true}
 *   discountPercentage={20}
 * />
 *
 * // Variante featured
 * <PriceDisplay price={99.99} variant="featured" />
 */
export const PriceDisplay = memo(function PriceDisplay({
  price,
  originalPrice,
  isOnSale = false,
  discountPercentage,
  offerDays,
  variant = 'default',
  showDiscountBadge = true,
  showOfferDays = true,
  className = ''
}: PriceDisplayProps) {
  const formatPrice = (value: number) => `Q${value.toFixed(2)}`;

  // Variante default (ProductCard)
  if (variant === 'default') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {isOnSale && originalPrice && (
          <span className="text-sm text-gray-400 line-through">
            {formatPrice(originalPrice)}
          </span>
        )}
        <span className={`text-lg font-semibold ${isOnSale ? 'text-red-600' : 'text-blue-600'}`}>
          {formatPrice(price)}
        </span>
        {isOnSale && showDiscountBadge && (
          <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
            OFERTA
          </span>
        )}
      </div>
    );
  }

  // Variante inline (para listas, tablas)
  if (variant === 'inline') {
    return (
      <div className={`inline-flex items-center gap-2 ${className}`}>
        {isOnSale && originalPrice && (
          <span className="text-sm text-gray-400 line-through">
            {formatPrice(originalPrice)}
          </span>
        )}
        <span className={`font-medium ${isOnSale ? 'text-red-600' : 'text-gray-900'}`}>
          {formatPrice(price)}
        </span>
      </div>
    );
  }

  // Variante featured (FeaturedProducts - con fondo)
  if (variant === 'featured') {
    if (isOnSale && originalPrice) {
      return (
        <div className={`bg-red-50 p-3 rounded-lg ${className}`}>
          <div className="flex items-baseline space-x-2 mb-1">
            <span className="text-sm text-gray-500 line-through">
              {formatPrice(originalPrice)}
            </span>
            <span className="text-xl font-bold text-red-600">
              {formatPrice(price)}
            </span>
            {showOfferDays && offerDays != null && offerDays > 0 && (
              <span className="text-xs text-gray-600">({offerDays} días)</span>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className={`bg-blue-50 p-3 rounded-lg ${className}`}>
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-xl font-bold text-blue-600">
            {formatPrice(price)}
          </span>
          <span className="text-sm font-medium text-gray-700">C/U</span>
        </div>
      </div>
    );
  }

  // Variante compact (NewProducts - con gradient)
  if (variant === 'compact') {
    if (isOnSale && originalPrice) {
      return (
        <div className={`bg-gradient-to-r from-red-50 to-red-100 p-3 rounded-lg border border-red-100 ${className}`}>
          <div className="flex flex-col space-y-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm text-gray-500 line-through">
                {formatPrice(originalPrice)}
              </span>
              <span className="font-bold text-red-600 text-lg">
                {formatPrice(price)}
              </span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`bg-blue-50 p-2 rounded-lg ${className}`}>
        <div className="flex items-baseline justify-between">
          <span className="font-semibold text-blue-600">{formatPrice(price)}</span>
          <span className="text-xs font-medium text-gray-700">C/U</span>
        </div>
      </div>
    );
  }

  // Variante large (ProductDetails)
  if (variant === 'large') {
    return (
      <div className={`bg-gradient-to-br from-blue-50 to-white p-6 rounded-xl border border-blue-100 ${className}`}>
        <div className="flex items-baseline gap-3">
          {isOnSale && originalPrice && (
            <span className="text-xl text-gray-400 line-through">
              {formatPrice(originalPrice)}
            </span>
          )}
          <span className={`text-3xl font-bold ${isOnSale ? 'text-red-600' : 'text-blue-600'}`}>
            {formatPrice(price)}
          </span>
          {isOnSale && showDiscountBadge && discountPercentage && (
            <span className="text-sm bg-red-100 text-red-600 px-3 py-1 rounded-full font-medium">
              -{discountPercentage}%
            </span>
          )}
        </div>
        {showOfferDays && offerDays != null && offerDays > 0 && (
          <p className="text-sm text-gray-600 mt-2">
            Oferta válida por {offerDays} días
          </p>
        )}
      </div>
    );
  }

  // Fallback al default
  return (
    <span className={`font-semibold text-blue-600 ${className}`}>
      {formatPrice(price)}
    </span>
  );
});

/**
 * Badge de descuento standalone
 */
interface DiscountBadgeProps {
  percentage: number;
  variant?: 'pill' | 'corner' | 'ribbon';
  className?: string;
}

export const DiscountBadge = memo(function DiscountBadge({
  percentage,
  variant = 'pill',
  className = ''
}: DiscountBadgeProps) {
  if (variant === 'pill') {
    return (
      <span className={`text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium ${className}`}>
        -{percentage}%
      </span>
    );
  }

  if (variant === 'corner') {
    return (
      <div className={`absolute top-0 right-0 z-10 ${className}`}>
        <div className="flex items-center bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1 rounded-bl-lg shadow-md">
          <Tag className="w-3 h-3 mr-1" />
          <span className="text-xs font-bold">{percentage}%</span>
          <span className="text-xs font-bold ml-0.5">OFF</span>
        </div>
      </div>
    );
  }

  if (variant === 'ribbon') {
    return (
      <div className={`absolute top-3 left-0 z-10 ${className}`}>
        <div className="bg-red-600 text-white text-xs font-bold px-3 py-1 shadow-md">
          -{percentage}%
        </div>
      </div>
    );
  }

  return null;
});

/**
 * Badge de "OFERTA" standalone
 */
interface OfferBadgeProps {
  text?: string;
  className?: string;
}

export const OfferBadge = memo(function OfferBadge({
  text = 'OFERTA',
  className = ''
}: OfferBadgeProps) {
  return (
    <span className={`text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium ${className}`}>
      {text}
    </span>
  );
});

export default PriceDisplay;
